export const SUSPEND_MODES = ['read_only', 'blocked'];
export const SUSPEND_CATEGORIES = ['non_payment', 'customer_request', 'security', 'compliance', 'other'];
export const FREEZE_CATEGORIES = ['security', 'compliance'];
export const TENANT_ENVIRONMENTS = ['live', 'demo'];
export const CLOSURE_ACCESS = ['everyone', 'admins'];
export const LIFECYCLE_STATES = ['archived', 'suspended', 'deletion_grace', 'closing', 'past_due', 'trial', 'active'];
export const SUSPEND_CATEGORY_LABELS = {
    non_payment: 'Payment overdue',
    customer_request: 'At your request',
    security: 'Security check',
    compliance: 'Compliance check',
    other: 'Account on hold',
};
function categoryOf(value) {
    return SUSPEND_CATEGORIES.includes(value ?? '') ? value : null;
}
function derived(state, status, flags, extra = {}) {
    return {
        status,
        readOnly: flags.readOnly,
        blocked: flags.blocked,
        readOnlyGrace: flags.readOnly && !flags.blocked && state !== 'archived',
        state,
        since: extra.since ?? null,
        until: extra.until ?? null,
        reasonCategory: extra.reasonCategory ?? null,
        deletionDueAt: extra.deletionDueAt ?? null,
    };
}
export function deriveLifecycle(axes, now) {
    if (axes.archivedAt)
        return derived('archived', 'archived', { readOnly: true, blocked: false }, { since: axes.archivedAt });
    if (axes.suspendedAt) {
        const blocked = axes.suspendMode === 'blocked';
        return derived('suspended', 'suspended', { readOnly: true, blocked }, { since: axes.suspendedAt, reasonCategory: categoryOf(axes.suspendCategory) ?? 'other' });
    }
    if (axes.deletionDueAt && (!axes.closureEndsAt || axes.closureEndsAt.getTime() <= now.getTime())) {
        return derived('deletion_grace', 'suspended', { readOnly: true, blocked: true }, { since: axes.closureEndsAt ?? axes.closureNoticeAt, until: axes.deletionDueAt, deletionDueAt: axes.deletionDueAt });
    }
    if (axes.closureNoticeAt) {
        return derived('closing', 'suspended', { readOnly: true, blocked: false }, { since: axes.closureNoticeAt, until: axes.closureEndsAt, deletionDueAt: axes.deletionDueAt });
    }
    if (axes.lapsedAt)
        return derived('past_due', 'past_due', { readOnly: true, blocked: false }, { since: axes.lapsedAt });
    if (!axes.activatedAt)
        return derived('trial', 'trial', { readOnly: false, blocked: false }, { until: axes.trialEndsAt });
    return derived('active', 'active', { readOnly: false, blocked: false });
}
export function lifecycleDtoOf(lifecycle, extra) {
    const iso = (value) => (value ? value.toISOString() : null);
    return {
        state: lifecycle.state,
        status: lifecycle.status,
        readOnly: lifecycle.readOnly,
        blocked: lifecycle.blocked,
        since: iso(lifecycle.since),
        until: iso(lifecycle.until),
        reasonCategory: lifecycle.reasonCategory,
        deletionDueAt: iso(lifecycle.deletionDueAt),
        trialEndsAt: iso(extra.trialEndsAt),
        paidUntil: iso(extra.paidUntil),
        pendingRenewalSince: iso(extra.pendingRenewalSince),
        frozen: lifecycle.state === 'suspended' && FREEZE_CATEGORIES.includes(lifecycle.reasonCategory ?? ''),
    };
}
export function lifecycleAccess(lifecycle, options) {
    switch (lifecycle.state) {
        case 'active':
        case 'trial':
            return { signIn: 'everyone', writes: true, export: true };
        case 'past_due':
        case 'closing':
            return {
                signIn: lifecycle.state === 'closing' && options.closureAccess === 'admins' ? 'admins_and_export' : 'everyone',
                writes: false,
                export: true,
            };
        case 'suspended': {
            if (!lifecycle.blocked)
                return { signIn: 'everyone', writes: false, export: true };
            const restricted = lifecycle.reasonCategory === 'security' || lifecycle.reasonCategory === 'compliance';
            return { signIn: 'super_admins', writes: false, export: !restricted || options.exportReleased === true };
        }
        case 'deletion_grace':
            return options.runtime === 'local' ? { signIn: 'admins_and_export', writes: false, export: true } : { signIn: 'nobody', writes: false, export: false };
        case 'archived':
            return { signIn: 'admins_and_export', writes: false, export: true };
    }
}
export const CLINICAL_CONTINUITY_PERMISSIONS = new Set([
    'emar.administer',
    'vitals.write',
    'nursing.note.write',
]);
export function continuityWriteAllowed(lifecycle, anyOf, allOf) {
    if (!lifecycle)
        return false;
    if (lifecycle.state !== 'past_due' && lifecycle.state !== 'suspended')
        return false;
    if (lifecycle.blocked || lifecycle.frozen)
        return false;
    if (anyOf.length === 0 && allOf.length === 0)
        return false;
    return [...anyOf, ...allOf].every((code) => CLINICAL_CONTINUITY_PERMISSIONS.has(code));
}
//# sourceMappingURL=tenant-lifecycle.js.map